import { getLastMessage } from './conversation';
import logger from './logger';

const normalizeTimestamp = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }

  // Matrix timestamps are in milliseconds (13+ digits)
  // If less than 1e12, assume it's in seconds and convert to ms
  const timestamp = value > 1e12 ? Math.floor(value) : Math.floor(value * 1000);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getConversationTimestamp = (conversation) => {
  if (!conversation) return 0;

  // PRIORITY 1: Use lastActivity (excludes profile-only changes like avatar/displayname)
  // This ensures profile metadata changes don't affect conversation sorting
  const activityTimestamp = normalizeTimestamp(conversation.lastActivity ?? 0);
  if (activityTimestamp > 0) {
    return activityTimestamp;
  }

  // PRIORITY 2: Fall back to lastMessage.timestamp if lastActivity not available
  const lastMessage = conversation.lastMessage;
  if (lastMessage && typeof lastMessage === 'object') {
    const rawTimestamp = [
      lastMessage.timestamp,
      lastMessage.ts,
      lastMessage.time,
    ].find(value => typeof value === 'number' && !Number.isNaN(value));

    const normalized = normalizeTimestamp(rawTimestamp ?? 0);
    if (normalized > 0) {
      return normalized;
    }
  }

  // PRIORITY 3: Legacy message fallback
  const legacyMessage = getLastMessage(conversation);
  if (legacyMessage) {
    const normalized = normalizeTimestamp(legacyMessage.timestamp ?? legacyMessage.ts ?? 0);
    if (normalized > 0) {
      return normalized;
    }
  }

  return 0;
};

/**
 * Sort contacts alphabetically by name (A-Z)
 * @param {Array} contacts - Array of contact objects
 * @returns {Array} - Sorted array of contacts
 */
export const sortContactsByName = (contacts) => {
  return [...contacts].sort((a, b) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    
    return 0;
  });
};

/**
 * Sort conversations by most recent message timestamp (recent first)
 * @param {Array} conversations - Array of conversation objects
 * @returns {Array} - Sorted array of conversations
 */
export const sortConversationsByRecentActivity = (conversations) => {
  return [...conversations].sort((a, b) => {
    if (a?.isInvite && !b?.isInvite) {
      return -1;
    }

    if (!a?.isInvite && b?.isInvite) {
      return 1;
    }

    const timestampA = getConversationTimestamp(a);
    const timestampB = getConversationTimestamp(b);

    if (timestampA !== timestampB) {
      return timestampB - timestampA;
    }

    const idA = (a?.id || '').toLowerCase();
    const idB = (b?.id || '').toLowerCase();

    if (idA && idB) {
      const idDiff = idA.localeCompare(idB);
      if (idDiff !== 0) {
        return idDiff;
      }
    }

    const nameA = (a?.name || a?.title || '').toLowerCase();
    const nameB = (b?.name || b?.title || '').toLowerCase();

    return nameA.localeCompare(nameB);
  });
};
